import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';

/**
 * Reusable date range picker with presets
 * Used across: Bookings, Financials, Bookings, Payroll, CompanyHealth
 */
const DateRangePicker = ({ 
  startDate, 
  endDate, 
  onDateRangeChange,
  presets = ['all', 'today', 'next7', 'next30', 'custom'],
  defaultPreset = 'all',
  showCard = true
}) => {
  const [activePreset, setActivePreset] = useState(defaultPreset);
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [customStart, setCustomStart] = useState(startDate || '');
  const [customEnd, setCustomEnd] = useState(endDate || '');

  const applyPreset = (preset) => {
    const today = new Date();
    let start, end;
    
    switch(preset) {
      case 'all':
        start = '';
        end = '';
        break;
      case 'today':
        start = end = today.toISOString().split('T')[0];
        break;
      case 'next7':
        start = today.toISOString().split('T')[0];
        end = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        break;
      case 'next30':
        start = today.toISOString().split('T')[0];
        end = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        break;
      case 'week':
        start = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        break;
      case 'month':
        start = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        break;
      case 'custom':
        setShowCustomPicker(true);
        setActivePreset('custom');
        return;
      default:
        return;
    }
    
    setActivePreset(preset);
    setShowCustomPicker(false);
    onDateRangeChange(start, end, preset);
  };

  const handleCustomApply = () => {
    if (customStart && customEnd) {
      setActivePreset('custom');
      setShowCustomPicker(false);
      onDateRangeChange(customStart, customEnd, 'custom');
    }
  };

  const presetLabels = {
    all: 'All Time',
    today: 'Today',
    next7: 'Next 7 Days',
    next30: 'Next 30 Days',
    week: 'Last 7 Days',
    month: 'Last 30 Days',
    custom: 'Custom'
  };

  const activeButtonStyle = { background: '#2563EB', color: 'white' };

  const content = (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium text-gray-700">Date Range:</span>
        <div className="flex gap-2 flex-wrap">
          {presets.map(preset => (
            <Button
              key={preset}
              variant={activePreset === preset ? 'default' : 'outline'}
              size="sm"
              onClick={() => applyPreset(preset)}
              style={activePreset === preset ? activeButtonStyle : {}}
            >
              {presetLabels[preset]}
            </Button>
          ))}
        </div>
      </div>
      
      {showCustomPicker && (
        <div className="flex flex-col md:flex-row items-end gap-3 pt-2 border-t">
          <div className="flex-1 w-full">
            <Label className="text-xs text-gray-600">Start Date</Label>
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-sm mt-1"
            />
          </div>
          <div className="flex-1 w-full">
            <Label className="text-xs text-gray-600">End Date</Label>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-sm mt-1"
            />
          </div>
          <Button
            size="sm"
            onClick={handleCustomApply}
            style={{ background: '#2563EB', color: 'white' }}
          >
            Apply Range
          </Button>
        </div>
      )}
      
      {!showCustomPicker && activePreset === 'custom' && startDate && endDate && (
        <div className="text-sm text-gray-600">
          Showing: {new Date(startDate).toLocaleDateString()} - {new Date(endDate).toLocaleDateString()}
        </div>
      )}
    </div>
  );

  return showCard ? (
    <Card>
      <CardContent className="pt-6">
        {content}
      </CardContent>
    </Card>
  ) : content;
};

export default DateRangePicker;
